import type { ApproveAndSendCommand, DeliveryView } from "@/api";

export interface WorkspaceSubmission {
  draftId: string;
  command: ApproveAndSendCommand;
}

/** No content or credentials: retain only the opaque command identity across a lost response/reload. */
export function submissionStorageKey(actorId: string, requestId: string) {
  return `reqventos:submission:${actorId}:${requestId}`;
}

export function pendingWorkspaceSubmission(delivery: DeliveryView | null): WorkspaceSubmission | null {
  if (delivery?.state !== "IDLE" || !delivery.draft_id || !delivery.operatorCommandKey
      || !delivery.approvedArtifactHash || !Number.isInteger(delivery.operatorExpectedVersion)
      || !Number.isInteger(delivery.operatorRequestRevision)) return null;
  return { draftId: delivery.draft_id, command: {
    idempotency_key: delivery.operatorCommandKey,
    expected_version: delivery.operatorExpectedVersion!,
    expected_request_revision: delivery.operatorRequestRevision!,
    expected_artifact_hash: delivery.approvedArtifactHash,
  } };
}

export function submissionForPreview(
  draftId: string, version: number, requestRevision: number, artifactHash: string,
  previous: WorkspaceSubmission | null, confirmTaskIds: string[] = [], createKey: () => string = () => crypto.randomUUID(),
): WorkspaceSubmission {
  // Approval increments the draft version while retaining the frozen artifact. A lost response
  // must still replay the original command even when a background read sees that increment.
  if (previous?.draftId === draftId
      && previous.command.expected_artifact_hash === artifactHash
      && previous.command.expected_request_revision === requestRevision) return previous;
  // The artifact hash covers the listed tasks (#2516), so a reused command names the same ones.
  return { draftId, command: { idempotency_key: createKey(), expected_version: version,
    expected_request_revision: requestRevision, expected_artifact_hash: artifactHash, confirm_task_ids: confirmTaskIds } };
}

export function readStoredSubmission(key: string): WorkspaceSubmission | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? "null");
    const command = value?.command;
    return typeof value?.draftId === "string" && typeof command?.idempotency_key === "string"
      && Number.isInteger(command.expected_version) && Number.isInteger(command.expected_request_revision)
      && typeof command.expected_artifact_hash === "string"
      && (command.confirm_task_ids === undefined || Array.isArray(command.confirm_task_ids)
        && command.confirm_task_ids.every((id: unknown) => typeof id === "string")) ? value : null;
  } catch { return null; }
}

export function storeSubmission(key: string, value: WorkspaceSubmission | null) {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch { /* In-memory identity and the server's durable operation still protect retries. */ }
}
